// 전자책 매니페스트 — 콘텐츠 주도(비개발자 갱신: public/content/book.json).
// 하드코딩 금지(CLAUDE.md). 이미지·오디오는 더미 선행, 실물은 교체만(docs/build/06).
// FR-BOOK-01/02/07/08.
import {
  loadAssetManifest,
  manifestEntry,
  type AssetManifest } from "../lib/assetManifest";
import {
  isOnWeb,
  firstOnWeb,
  normalizeBookGoods,
  mergeBookLists,
  bookAudioKey,
  bookAudioLegacyKey,
  arTargetKey,
  arLayerOf,
  arLayerModelOf,
  normalizeArInteract,
  magicLayerKeys,
  magicSceneCountByBook,
  type ArInteract,
  magicModelKeys,
  pageHasMagic,
} from "@greg-chick/shared";
import { EDGE_BASE } from "../lib/apiBase";
import { fetchEarlyOr } from "../lib/earlyFetch";
import { safeImageUrl, safeVideoUrl, safeAudioUrl, safeHttpUrl } from "../lib/safeUrl";
// 상대 좌표(0~1)·페이지 비율 단일 정의는 packages/shared. FR-ADMIN-16.
import {
  normalizeAspect,
  bookCoverKey,
  bookPageImageKey,
  bookSummaryFromDoc,
  type LayoutPos,
  type BookSummary,
} from "@greg-chick/shared";

export type BookAccess = "full" | "gated";

export type { LayoutPos };

export interface BookPage {
  id: string;
  cover?: boolean;
  image?: string; // WebP 긴변 2048·≤300KB(FR-BOOK-08). 없으면 더미 SVG 렌더.
  video?: string; // 유튜브 URL/ID — youtube-nocookie 임베드(아동 안전). 클릭 후 재생.
  videoFile?: string; // 업로드한 동영상 파일(mp4/webm) URL. 유튜브보다 우선. 클릭 후 재생.
  alt_ko?: string;
  motionText?: string; // 의성어·의태어(FR-BOOK-06)
  /** 영어 의성어 — 자막과 같은 규칙으로 언어를 따라간다. 없으면 한글 의성어를 그대로 쓴다. */
  motionText_en?: string;
  /**
   * 영어 의성어를 놓을 자리. 안 정했으면 한글 자리(`motionPos`)를 그대로 쓴다.
   *
   * 왜 따로 두나: 「쿵!」과 「Thump, thump!」는 **길이가 딴판**이라 같은 자리에 놓으면
   * 한쪽이 그림 밖으로 나가거나 인물을 가린다.
   */
  motionPos_en?: { x: number; y: number };
  motionPos?: LayoutPos; // 모션텍스트 위치(없으면 기본 CSS 위치)
  subtitle_ko?: string;
  subtitle_en?: string;
  subtitlePos?: LayoutPos; // 자막을 페이지 위로 올릴 위치(없으면 하단 캡션)
  aspect?: string; // 페이지 가로세로 비율(CSS aspect-ratio "W/H"). 없으면 기본 3/4
  audio_ko?: string; // 지연 로드(FR-BOOK-03). 더미엔 없음 → "준비중".
  audio_en?: string;
  ar?: boolean; // 이 페이지에 AR 타깃 게시됨(ar.target.<책>.<페이지>) → 뷰어에 "AR로 보기"
  /**
   * 🪄 **「누르면 일어나는 마법」**(docs/build/35) — 카메라 없이 쪽 그림 위에서 그대로 논다.
   * 정의는 타깃 항목의 `interact`, 그림은 `ar.layer.img.<층>.<장면>`. 둘 다 게시된 것만.
   * 카톡 인앱·교실 태블릿·카메라 거부에서도 스토리보드가 산다. 관리자 미리보기가 이 코드다.
   */
  magic?: ArInteract;
  /** 층 id → 그림. 스트립이면 `fps`(가로 정사각 N프레임 — 기존 규격). */
  magicLayers?: Record<string, { url: string; fps?: number; once?: boolean }>;
  /** 🧊 층별 3D 파일(.glb) — 있으면 「✨ 마법 보기」가 띠 대신 진짜 3D 를 세운다(카메라 AR 과 같은 규칙) */
  magicModels?: Record<string, string>;
}

export interface BookManifest {
  id: string;
  title_ko: string;
  title_en?: string;
  access: BookAccess;
  previewPages: number; // 게이팅 시 무료 공개 페이지 수
  viewMode?: "single" | "spread"; // 보기: 한 장씩 / 두 장씩(좁은 화면은 자동 단면). 없으면 두 장씩(폭 기준)
  /** 다 읽은 뒤 보여줄 굿즈 id(최대 3) — 관리자 [📖 전자책]에서 고른다. */
  goods: string[];
  pages: BookPage[];
}

/** 외부 JSON 을 안전한 매니페스트로 정규화(손상·누락 방어). 절대 throw 안 함. */
export function normalizeManifest(input: unknown): BookManifest {
  const o = (input ?? {}) as Record<string, unknown>;
  const rawPages = Array.isArray(o.pages) ? o.pages : [];
  const pages: BookPage[] = rawPages
    .map((p, i): BookPage | null => {
      if (!p || typeof p !== "object") return null;
      const pg = p as Record<string, unknown>;
      const id = typeof pg.id === "string" && pg.id ? pg.id : `p${i}`;
      return {
        id,
        cover: pg.cover === true,
        image: safeImageUrl(typeof pg.image === "string" ? pg.image : undefined),
        video: str(pg.video),
        videoFile: safeVideoUrl(typeof pg.videoFile === "string" ? pg.videoFile : undefined),
        alt_ko: str(pg.alt_ko),
        motionText: str(pg.motionText),
        motionText_en: str(pg.motionText_en),
        motionPos: pos(pg.motionPos),
        motionPos_en: pos(pg.motionPos_en),
        subtitle_ko: str(pg.subtitle_ko),
        subtitle_en: str(pg.subtitle_en),
        subtitlePos: pos(pg.subtitlePos),
        aspect: normalizeAspect(pg.aspect),
        audio_ko: str(pg.audio_ko),
        audio_en: str(pg.audio_en),
      };
    })
    .filter((p): p is BookPage => p !== null);

  return {
    id: typeof o.id === "string" ? o.id : "book",
    title_ko: typeof o.title_ko === "string" ? o.title_ko : "전자책",
    title_en: str(o.title_en),
    access: o.access === "gated" ? "gated" : "full",
    viewMode: o.viewMode === "single" || o.viewMode === "spread" ? o.viewMode : undefined,
    previewPages:
      typeof o.previewPages === "number" && o.previewPages >= 0
        ? Math.floor(o.previewPages)
        : Math.max(1, Math.min(3, pages.length)),
    // 판정은 shared 한 곳 — 관리자 고르기와 같은 규칙이라야 "관리자엔 있는데 화면엔 없다"가 안 난다
    goods: normalizeBookGoods(o.goods),
    pages,
  };
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

/** 상대 좌표 파싱(0~1 클램프). 손상값은 undefined → 기본 위치 폴백. */
function pos(v: unknown): LayoutPos | undefined {
  if (!v || typeof v !== "object") return undefined;
  const o = v as Record<string, unknown>;
  if (typeof o.x !== "number" || typeof o.y !== "number") return undefined;
  if (!Number.isFinite(o.x) || !Number.isFinite(o.y)) return undefined;
  const c = (n: number) => Math.max(0, Math.min(1, n));
  return { x: c(o.x), y: c(o.y) };
}

/** 고르기 화면 카드 한 장. `cover` 는 매니페스트에서 풀어낸 **표시용 URL**(서버 응답엔 없다). */
export interface BookListItem extends BookSummary {
  cover?: string;
  /**
   * ✨ 이 책에 **아이가 쓸 수 있는** 마법이 몇 쪽인가(0이면 없다).
   * 책을 열지 않고 매니페스트만으로 센다(`magicSceneCountByBook`) — 고르는 화면이 세 권을
   * 다 받아 오면 느려진다. 「이 책엔 마법이 있어요」를 **들어가 보기 전에** 알려 주려고 있다.
   */
  magicPages?: number;
}

/** 목록 원본만 가져온다(표지 URL 은 아직 안 붙음). */
/**
 * 아이에게 보여줄 책 목록 — **저장소 목록 + 게시본을 합친다.**
 *
 * 🔴 예전에는 게시본이 한 권이라도 있으면 저장소 목록을 통째로 버렸다. 그래서 운영자가
 * [+ 새 전자책] 으로 **빈 책 하나를 저장한 순간** 「나무 위 도서관」·「그레그와 병아리의 경주」가
 * **둘 다 사라졌다**(2026-08-21 신고). 지운 적도 없는데, 에러도 없이.
 *
 * 합치는 규칙은 shared `mergeBookLists` 한 곳 — 관리자도 같은 함수를 쓴다.
 * (이 저장소는 그림에서 이미 「정적 기본 + 게시본 덮어쓰기」를 쓴다. 책도 같게 맞췄다.)
 */
async function fetchBooksRaw(): Promise<BookSummary[]> {
  const get = async (url: string, init: RequestInit): Promise<BookSummary[]> => {
    try {
      // 게시본 목록은 index.html 이 먼저 띄워 뒀을 수 있다(lib/earlyFetch — 표지가 이 목록을 기다린다)
      const res = await fetchEarlyOr(url, init);
      if (!res.ok) return [];
      return ((await res.json()) as { books?: BookSummary[] }).books ?? [];
    } catch {
      return []; // 서버가 없어도 목록은 나와야 한다(불변식 3)
    }
  };
  const [published, repo] = await Promise.all([
    get(`${EDGE_BASE}/api/books`, { cache: "no-store" }),
    get("/content/books.json", { cache: "force-cache" }),
  ]);
  const merged = mergeBookLists(repo, published);
  if (merged.length > 0) return merged;

  // 아주 오래된 콘텐츠(한 권짜리 book.json)를 위한 마지막 그물
  try {
    const res = await fetch("/content/book.json", { cache: "force-cache" });
    if (res.ok) {
      const d = (await res.json()) as Record<string, unknown>;
      return [bookSummaryFromDoc(typeof d.id === "string" ? d.id : "main", d)];
    }
  } catch {
    /* empty */
  }
  return [];
}

/** 저장된 전자책 목록(고르기 화면). 엣지 /api/books → 정적 폴백(M1) + 표지 그림 병합.
 *  표지는 전용 표지(`book.cover.<id>`) → 표지 페이지 그림 순. 둘 다 없으면 undefined
 *  (화면이 제목으로 절차적 표지를 그린다 — 빈 상자를 보여 주지 않는다). */
export function loadBooksList(): Promise<BookListItem[]> {
  // 앱이 뜰 때 미리 받아 둔 것이 있으면 **한 번만** 쓴다 — 두 번째부터는 늘 새로 받는다(운영자가 고친 목록이 보이게)
  const warm = warmList;
  warmList = null;
  return warm ?? fetchBooksList();
}

/**
 * ⏱ **책장 주소로 들어왔으면 앱이 뜨자마자 목록을 받기 시작한다** (2026-09-26 · 조치계획서 ■2).
 *
 * 예전엔 책장 화면 조각(Book-*.js)이 **다 받힌 뒤에야** 목록·그림 목록을 물었다 — 운영 시간표에서
 * 조각을 받는 70ms 동안 회선이 놀았고, 표지는 그 뒤로 밀렸다(광랜 「다 받음」 0.71초, 목표 0.6초).
 * 로더는 이미 첫 코드 안에 있으니 **무게는 늘지 않는다.** 부르는 곳은 `main.tsx` 한 곳.
 */
export function warmBooksList(): void {
  if (!warmList) warmList = fetchBooksList();
}
let warmList: Promise<BookListItem[]> | null = null;

async function fetchBooksList(): Promise<BookListItem[]> {
  const [books, manifest] = await Promise.all([
    fetchBooksRaw(),
    // 매니페스트를 못 읽어도 목록은 나와야 한다 — 표지만 없는 상태로 진행
    loadAssetManifest().catch(() => ({ version: 0, assets: {} })),
  ]);
  // ✨ 마법 쪽 수 — 매니페스트 한 번으로 세 권을 다 센다(책 문서는 안 받는다). 규칙은 shared.
  const magicByBook = magicSceneCountByBook(manifest);
  return books.map((b) => {
    // 🔴 `??` 로 고르면 **[준비 지우기] 로 비운 자리(`{ url: "" }`)가 멀쩡한 그림을 가린다** —
    //    빈 자리도 객체라 참이다. 그러면 「전용 표지를 지웠더니 표지가 통째로 사라진다」가 된다.
    //    진짜 나가는 첫 자리를 고르는 일은 shared 한 곳(`firstOnWeb`).
    const cover = firstOnWeb(
      manifestEntry(manifest, bookCoverKey(b.id)),
      b.coverPageId ? manifestEntry(manifest, bookPageImageKey(b.id, b.coverPageId)) : undefined,
    );
    return { ...b, cover: safeImageUrl(cover?.url), magicPages: magicByBook[b.id] ?? 0 };
  });
}

/** 전자책 1권 로드(서버 불필요). 게시본(엣지 /api/book?id=) 우선 → 정적 /content/book.json 폴백.
 *  페이지 이미지·동영상(asset-manifest `book.page.<bookId>.<pageId>`)을 병합 — 구버전 비네임스페이스 키도 폴백. */
/**
 * 게시된 그림·동영상·소리를 책 문서에 **얹는다**(책 문서에 직접 적힌 값이 우선).
 *
 * 🔴 **밖으로 뺀 이유**: 예전에는 이 병합이 `loadBook` 안에 있어서 검사하려면 서버 응답을
 * 흉내내야 했다 — 그래서 아무도 안 쟀다. 그 사이 자리 이름을 **손으로 이어 붙인 채**로
 * 남아 있었다(관리자·점검 도구는 shared 를 쓰는데 뷰어만). 모양이 한 번 바뀌면
 * **관리자는 「있다」는데 아이 화면에서만 소리가 안 난다.**
 *
 * @param ns 자리 이름의 책 이름 칸(책 id)
 */
/**
 * 🔊 **그 쪽의 게시된 읽어주기 소리** — 책 화면과 카메라 AR(리서치 최종안 ⑨)이 **같이** 부른다.
 *
 * 🔴 두 화면이 따로 찾으면 「책에선 나는데 카메라에선 안 난다」가 아무 에러 없이 생긴다
 * (이 저장소가 여러 번 겪은 모양 — 자리 이름은 shared `bookAudioKey`, 고르기는 여기 한 곳).
 * 옛 자리(책 이름 없는 키)도 보고, 비운 자리(`{url:""}`)는 건너뛴다(`firstOnWeb`). 주소는 한 번 더 거른다(불변식 7).
 */
export function publishedPageAudio(
  manifest: AssetManifest,
  ns: string,
  pageId: string,
): { ko?: string; en?: string } {
  const out: { ko?: string; en?: string } = {};
  for (const lang of ["ko", "en"] as const) {
    const entry = firstOnWeb(
      manifestEntry(manifest, bookAudioKey(ns, pageId, lang)),
      manifestEntry(manifest, bookAudioLegacyKey(pageId, lang)),
    );
    const url = entry ? safeAudioUrl(entry.url) : undefined;
    if (url) out[lang] = url;
  }
  return out;
}

export function mergePublishedAssets(
  book: BookManifest,
  ns: string,
  manifest: AssetManifest,
): BookManifest {
    for (const p of book.pages) {
      // 비운 자리가 옛 자리를 가리지 않게 — 진짜 나가는 첫 자리를 고른다(`firstOnWeb`)
      const entry = firstOnWeb(
        manifestEntry(manifest, bookPageImageKey(ns, p.id)),
        manifestEntry(manifest, `book.page.${p.id}`),
      );
      if (entry) {
        if (!p.image) p.image = safeImageUrl(entry.url); // 게시 이미지 우선(book.json 직접 지정이 있으면 그대로)
        if (entry.alt && !p.alt_ko) p.alt_ko = entry.alt;
      }
      const ventry = firstOnWeb(
        manifestEntry(manifest, `book.pagevideo.${ns}.${p.id}`),
        manifestEntry(manifest, `book.pagevideo.${p.id}`),
      );
      if (ventry && !p.videoFile) p.videoFile = safeVideoUrl(ventry.url); // 게시된 동영상 파일 병합
      /**
       * 게시된 읽어주기(한국어/영어) 병합 → 뷰어 🔊 재생. 옛 키(책 이름 없는 것)도 본다.
       *
       * 🔴 **자리 이름은 만드는 곳에서 가져온다**(shared `bookAudioKey`). 예전엔 여기서
       * 문자열을 직접 이어 붙였다 — 관리자·점검 도구는 shared 를 쓰는데 뷰어만 손으로 짠 것이다.
       * 모양이 한 번 바뀌면 **관리자와 `pnpm book:audio` 는 「있다」고 하는데 아이 화면에서만
       * 소리가 안 난다.** 아무 오류도 안 나서 「이 쪽은 원래 소리가 없나 보다」로 넘어간다.
       */
      const heard = publishedPageAudio(manifest, ns, p.id);
      if (heard.ko && !p.audio_ko) p.audio_ko = heard.ko;
      if (heard.en && !p.audio_en) p.audio_en = heard.en;
      /**
       * 페이지 통합 AR — 타깃이 게시돼 있으면 뷰어에 "AR로 보기"(딥링크 `/ar?scene=<책>.<쪽>`).
       * 책 쪽 AR 장면 id 는 `<책>.<쪽>` — 이름 만드는 일은 shared 에 맡긴다.
       *
       * 🔴 **자리가 있는 것과 그림이 있는 것은 다르다.**
       * 예전엔 자리(엔트리)만 있으면 참으로 봤다. 그런데 관리자에서 [준비 지우기]를 누르면
       * 자리는 남고 주소만 빈다(`{ url: "" }`) — 되살릴 수 있게 일부러 그렇게 둔다.
       * 그러면 아이 화면에는 **[AR로 보기]가 그대로 뜨고**, 누르면 카메라만 켜진 채
       * 아무 일도 안 일어난다. 「눌리는데 아무 일 없는 버튼」 — 이 저장소가 제일 싫어하는 것이다.
       * (운영에 실제로 그런 자리가 있다: `ar.target.ar_chumseongdae = { url: "" }`.)
       *
       * 판정은 shared 한 곳(`isOnWeb`) — 주소가 있고, 지워지지 않았고, 웹에서 안 내려간 것.
       */
      if (isOnWeb(manifestEntry(manifest, arTargetKey(`${ns}.${p.id}`)))) p.ar = true;

      /**
       * 🪄 **마법(층·누르는 자리·손잡이)** — 타깃 항목의 `interact` 에서 읽는다(docs/build/35).
       * 외부 입력이라 `normalizeArInteract` 를 지난 것만 쓴다(불변식 7).
       * 층 그림은 `ar.layer.img.<층>.<장면>` — 이름을 되읽는 일은 shared(`arLayerOf`).
       *
       * 🔴 **그림이 한 장도 안 게시됐으면 마법이 아니다.** 정의만 있고 그림이 없으면 아이 화면에
       * 「✨ 마법 보기」가 뜨는데 누르면 아무것도 안 나온다 — 「눌리는데 아무 일 없는 버튼」이다.
       * 자리가 있어도 주소가 빈 것(지운 것·내린 것)은 `isOnWeb` 이 거른다.
       */
      const sceneId = `${ns}.${p.id}`;
      const magic = normalizeArInteract(manifestEntry(manifest, arTargetKey(sceneId))?.interact);
      if (magic) {
        const layers: Record<string, { url: string; fps?: number; once?: boolean }> = {};
        // 🔴 **어느 층이 진짜 나가나**는 shared 한 곳(`magicLayerKeys`) — 고르는 화면이 「✨ 마법 2쪽」을
        //    셀 때 쓰는 규칙과 **같아야** 한다. 여기서 다시 짜면 「목록엔 2쪽인데 그 쪽엔 단추가 없다」가 된다.
        for (const k of magicLayerKeys(manifest, sceneId)) {
          const l = arLayerOf(k)!;
          const e = manifestEntry(manifest, k);
          const url = safeImageUrl(e!.url);
          if (!url) continue;
          layers[l.layerId] = {
            url,
            ...(typeof e!.fps === "number" && Number.isFinite(e!.fps) ? { fps: e!.fps } : {}),
            // 한 번만 재생(빵 한 입) — 카메라 AR(arData)와 같은 규칙으로 읽는다
            ...(e!.once === true ? { once: true } : {}),
          };
        }
        // 🧊 3D 파일 — 어느 것이 나가나는 shared 한 곳(`magicModelKeys`: 게시됨·`.glb`). 주소는 한 번 더 거른다(불변식 7).
        const models: Record<string, string> = {};
        for (const k of magicModelKeys(manifest, sceneId)) {
          const url = safeHttpUrl(manifestEntry(manifest, k)?.url);
          if (url) models[arLayerModelOf(k)!.layerId] = url;
        }
        // 🔴 층 그림 **또는** 3D 가 있어야 마법이다 — 고르는 화면이 세는 규칙과 같은 함수(shared pageHasMagic)
        if (pageHasMagic({ magic, magicLayers: layers, magicModels: models })) {
          p.magic = magic;
          p.magicLayers = layers;
          if (Object.keys(models).length > 0) p.magicModels = models;
        }
      }
    }

  return book;
}

/**
 * 📖 책 하나 — **못 받았는지까지** 돌려준다.
 *
 * 🔴 「쪽이 0개」는 두 가지다: **아직 안 만든 책**과 **지금 못 불러온 책**.
 * 둘을 뭉개면 아이가 보는 말은 「아직 준비된 페이지가 없어요」다 —
 * 사실은 와이파이가 끊겼거나 엣지가 아픈 것이고, 책은 멀쩡하다.
 * 부모는 「아직 안 만들었구나」 하고 창을 닫는다(전시장·부모 화면과 같은 계열).
 *
 * `fetchPublishedFirst` 는 엣지와 정적 파일 **둘 다** 실패하면 `null` 을 준다 —
 * 그걸 이미 알고 있었고 **버리고** 있었다.
 */
export async function loadBookEx(
  bookId?: string,
): Promise<{ book: BookManifest; offline: boolean; notFound?: boolean }> {
  try {
    /**
     * 🔴 **「그런 책이 없다」와 「못 물어봤다」를 가른다**(2026-10-11 운영에서 걸어 보고).
     *
     * 예전엔 게시본·저장소 둘 다 못 주면 전부 「못 불러왔어요 — 잠시 뒤 다시」였다. 그런데 `?id=없는책` 이면
     * 서버는 **분명히 404 로 답했다** — 연결 문제가 아니다. 잘못 인쇄된 QR·틀린 링크를 연 보호자는
     * 기다렸다 다시 열기만 되풀이한다. 서버가 404 를 줬고 저장소에도 없으면 「없다」로 말한다.
     */
    let edgeSaidMissing = false;
    const getEdge = async (): Promise<unknown | null> => {
      try {
        // 주소는 물음표까지 그대로 적는다 — 엣지 프리플라이트 관문(corsCoverage)이 주소 모양을 읽는다
        const r = await fetch(bookId ? `${EDGE_BASE}/api/book?id=${encodeURIComponent(bookId)}` : `${EDGE_BASE}/api/book`, {
          cache: "no-store",
        });
        if (r.status === 404) edgeSaidMissing = true;
        return r.ok ? await r.json() : null;
      } catch {
        return null; // 닿지 못했다 — 「없다」가 아니다(아래에서 저장소로 넘어간다)
      }
    };
    const getStatic = async (): Promise<unknown | null> => {
      try {
        // 정적 폴백도 책마다 따로 둔다(`/content/book.<id>.json`). 없으면 예전 단일 파일.
        const r = await fetch(bookId ? `/content/book.${bookId}.json` : "/content/book.json", { cache: "force-cache" });
        return r.ok ? await r.json() : null; // 없는 파일은 화면 HTML(200)로 와서 json 에서 던진다 → null
      } catch {
        return null;
      }
    };
    const [data, manifest] = await Promise.all([
      getEdge().then((d) => d ?? getStatic()),
      loadAssetManifest(),
    ]);
    // 둘 다 못 줬다 — 서버가 「없다」고 했으면 없는 책, 아니면 「빈 책」이 아니라 「못 물어봤다」
    if (!data) return { book: normalizeManifest({ pages: [] }), offline: !edgeSaidMissing, notFound: edgeSaidMissing };
    const book = normalizeManifest(data);
    /**
     * 🔴 **그림을 찾을 자리 이름은 「받은 책」의 것이다.**
     *
     * 예전엔 **요청한 id** 를 먼저 썼다. 그런데 서버가 요청한 것과 **다른 책**을 줄 때가
     * 있다 — 예를 들어 id 가 모양에 안 맞으면(`?id=없는책`) 엣지가 「id 없음」으로 보고
     * 기본 책을 준다. 그러면 화면에는 그 책이 뜨는데 그림은 **요청한 이름**으로 찾으므로
     * 하나도 안 나온다: 34쪽 전부 **「그림 준비중」**(운영 실측).
     *
     * 문서가 스스로 밝힌 id 를 먼저 쓴다. 그게 없을 때만 요청한 id 를 쓴다 —
     * `normalizeManifest` 가 id 없는 문서에 `"book"` 을 채워 넣기 때문에 **정규화 전 값**을 본다
     * (책마다 따로 둔 정적 폴백은 id 가 없을 수 있고, 그때는 요청한 id 가 맞다).
     */
    const declaredId = typeof (data as { id?: unknown }).id === "string" ? (data as { id: string }).id : "";
    return {
      book: mergePublishedAssets(book, declaredId || bookId || book.id, manifest),
      offline: false,
      notFound: false,
    };
  } catch {
    return { book: normalizeManifest({ pages: [] }), offline: true, notFound: false };
  }
}

/** 책 하나. 왜 비었는지까지 필요하면 `loadBookEx`. */
export async function loadBook(bookId?: string): Promise<BookManifest> {
  return (await loadBookEx(bookId)).book;
}

// ── 페이징(펼침/단면) — FR-BOOK-01 ──────────────────────────
export type ViewMode = "single" | "spread";

/** 표시 단위(leaf). 단면=페이지 1장, 펼침=표지 단독 후 2장씩. */
export function computeLeaves(pages: BookPage[], mode: ViewMode): BookPage[][] {
  if (pages.length === 0) return [];
  if (mode === "single") return pages.map((p) => [p]);

  const leaves: BookPage[][] = [];
  let i = 0;
  if (pages[0]?.cover) {
    leaves.push([pages[0]!]); // 표지는 펼침에서도 단독
    i = 1;
  }
  for (; i < pages.length; i += 2) {
    const pair = pages.slice(i, i + 2);
    leaves.push(pair);
  }
  return leaves;
}

export function clampLeaf(index: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(index, total - 1));
}

/** 실제 보기 모드 결정 — 리더 선택(userMode)이 자동(폭 기준)보다 우선.
 *  "한 장"은 항상 단면, "두 장"은 넓은 화면에서만(좁으면 두 장 무의미). 미선택이면 자동. */
export function resolveViewMode(
  userMode: ViewMode | null,
  autoMode: ViewMode,
  wide: boolean,
): ViewMode {
  if (userMode === "single") return "single";
  if (userMode === "spread") return wide ? "spread" : autoMode;
  return autoMode;
}

/** 현재 leaf ±2 범위의 페이지 인덱스(이미지 프리로드용) — FR-BOOK-02. */
export function preloadPageIndices(
  leafIndex: number,
  leaves: BookPage[][],
  pages: BookPage[],
): number[] {
  const ids = new Set<string>();
  for (let i = leafIndex - 2; i <= leafIndex + 2; i++) {
    leaves[i]?.forEach((p) => ids.add(p.id));
  }
  const out: number[] = [];
  pages.forEach((p, idx) => {
    if (ids.has(p.id)) out.push(idx);
  });
  return out;
}

// ── 접근 제어 — FR-BOOK-07 ──────────────────────────────────
// 기본 전권 공개. access:"gated" 시 previewPages 이후 잠금.
// ⚠️ 클라 게이팅은 "설계 훅"일 뿐 — 실제 권위 검증은 서버/엣지 서명(M2). ?k= 존재만으로 해제(placeholder).
export function isPageLocked(
  pageIndex: number,
  manifest: Pick<BookManifest, "access" | "previewPages">,
  keyProvided: boolean,
): boolean {
  if (manifest.access !== "gated") return false;
  if (keyProvided) return false;
  return pageIndex >= manifest.previewPages;
}

/** leaf 가 하나라도 잠긴 페이지를 포함하면 잠금 표시. */
export function isLeafLocked(
  leaf: BookPage[],
  pages: BookPage[],
  manifest: Pick<BookManifest, "access" | "previewPages">,
  keyProvided: boolean,
): boolean {
  return leaf.some((p) => isPageLocked(pages.indexOf(p), manifest, keyProvided));
}
